import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, X, CheckCheck, Package, MessageSquare, DollarSign, CheckCircle2, Clock, FileText, Megaphone, AlertTriangle, Info, Zap, ArrowRight } from "lucide-react";
import { captureException } from "@/lib/sentry";

const BG = "#0a0a0a";
const SURFACE = "#111111";
const SURFACE2 = "#161616";
const BORDER = "#1e1e1e";
const GREEN = "#10b981";
const AMBER = "#f59e0b";
const RED = "#ef4444";
const PURPLE = "#a855f7";
const BLUE = "#3b82f6";
const CYAN = "#06b6d4";

interface ActivityEvent {
  id: number;
  eventType: string;
  entityId: number | null;
  entityType: string | null;
  summary: string;
  metadata: string | null;
  isRead: boolean;
  createdAt: string;
}

interface AdminAnnouncement {
  id: number;
  title: string;
  body: string;
  priority: string;
  isRead: boolean;
  createdAt: string;
  expiresAt: string | null;
}

interface FeedItem {
  kind: "event" | "announcement";
  id: number;
  isRead: boolean;
  createdAt: string;
  data: ActivityEvent | AdminAnnouncement;
}

function getEventIcon(type: string): React.ReactNode {
  switch (type) {
    case "new_booking": return <Package size={14} color={GREEN} />;
    case "new_contact": return <MessageSquare size={14} color={BLUE} />;
    case "deposit_paid": return <DollarSign size={14} color={GREEN} />;
    case "booking_closeout": return <CheckCircle2 size={14} color={CYAN} />;
    case "waitlist_signup": return <Clock size={14} color={AMBER} />;
    case "release_form_submitted": return <FileText size={14} color={PURPLE} />;
    case "artist_available": return <Zap size={14} color={GREEN} />;
    case "artist_unavailable": return <Zap size={14} color={RED} />;
    case "ngu_new_lead": return <MessageSquare size={14} color={CYAN} />;
    case "ngu_lead_status_change": return <Zap size={14} color={AMBER} />;
    case "ngu_lead_won": return <DollarSign size={14} color={GREEN} />;
    case "ngu_lead_lost": return <X size={14} color={RED} />;
    default: return <Bell size={14} color="#666" />;
  }
}

function getAnnouncementIcon(priority: string): React.ReactNode {
  switch (priority) {
    case "critical": return <AlertTriangle size={14} color={RED} />;
    case "warning": return <AlertTriangle size={14} color={AMBER} />;
    default: return <Info size={14} color={BLUE} />;
  }
}

function getAnnouncementAccent(priority: string): string {
  switch (priority) {
    case "critical": return RED;
    case "warning": return AMBER;
    default: return BLUE;
  }
}

function getEventLink(eventType: string, adminToken: string, scope: "studio" | "ngu", metadata?: Record<string, unknown> | null): string | null {
  if (scope === "ngu") {
    switch (eventType) {
      case "ngu_new_lead":
      case "ngu_lead_status_change":
      case "ngu_lead_won":
      case "ngu_lead_lost":
        return `/admin/ngu-pipeline/${adminToken}`;
      default:
        return null;
    }
  }
  switch (eventType) {
    case "new_booking": {
      const mt = metadata?.manageToken as string | null | undefined;
      return mt ? `/admin/action/${mt}` : `/admin/today/${adminToken}`;
    }
    case "release_form_submitted": {
      const mt = metadata?.manageToken;
      return mt ? `/admin/action/${mt}#release-form` : `/admin/today/${adminToken}`;
    }
    case "new_contact":
      return `/admin/contact-inbox/${adminToken}`;
    case "deposit_paid":
      return `/admin/deposits/${adminToken}`;
    case "booking_closeout":
      return `/admin/recently-completed/${adminToken}`;
    case "waitlist_signup":
      return `/admin/waitlist/${adminToken}`;
    case "artist_available":
    case "artist_unavailable":
      return `/admin/availability/${adminToken}`;
    default:
      return null;
  }
}

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const SUSPENSION_SYNTHETIC_ID = -999;

interface ActivityCenterProps {
  adminKey: string;
  scope?: "studio" | "ngu";
  suspended?: boolean;
}

export function ActivityCenter({ adminKey, scope = "studio", suspended = false }: ActivityCenterProps) {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [feed, setFeed] = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const headers = { "x-admin-key": adminKey };
  const scopeParam = scope === "ngu" ? "&scope=ngu" : "";

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await fetch(`/api/admin/activity/unread-count?${scope === "ngu" ? "scope=ngu" : ""}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setUnread(data.count ?? 0);
      }
    } catch (_swallowedErr) { captureException(_swallowedErr); }
  }, [adminKey, scope]);

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/activity/feed?limit=60${scopeParam}`, { headers });
      if (res.ok) {
        const data: { events: ActivityEvent[]; announcements: AdminAnnouncement[] } = await res.json();
        const items: FeedItem[] = [
          ...data.announcements.map((a) => ({
            kind: "announcement" as const,
            id: a.id,
            isRead: a.isRead,
            createdAt: a.createdAt,
            data: a,
          })),
          ...data.events.map((e) => ({
            kind: "event" as const,
            id: e.id,
            isRead: e.isRead,
            createdAt: e.createdAt,
            data: e,
          })),
        ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        setFeed(items);
      }
    } catch (_swallowedErr) { captureException(_swallowedErr); }
    setLoading(false);
  }, [adminKey, scope]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60_000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (open) fetchFeed();
  }, [open, fetchFeed]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const markItemRead = async (item: FeedItem) => {
    if (item.isRead) return;
    try {
      const url = item.kind === "event"
        ? `/api/admin/activity/${item.id}/read`
        : `/api/admin/announcements/${item.id}/read`;
      await fetch(url, { method: "PATCH", headers });
      setFeed((prev) => prev.map((f) => f.kind === item.kind && f.id === item.id ? { ...f, isRead: true } : f));
      setUnread((prev) => Math.max(0, prev - 1));
    } catch (_swallowedErr) { captureException(_swallowedErr); }
  };

  const markAllRead = async () => {
    setMarkingAll(true);
    try {
      await fetch(`/api/admin/activity/read-all?${scope === "ngu" ? "scope=ngu" : ""}`, { method: "POST", headers });
      setFeed((prev) => prev.map((f) => ({ ...f, isRead: true })));
      setUnread(0);
    } catch (_swallowedErr) { captureException(_swallowedErr); }
    setMarkingAll(false);
  };

  // Synthetic suspension feed item — always pinned at top when suspended
  const suspensionItem: FeedItem | null = suspended ? {
    kind: "announcement",
    id: SUSPENSION_SYNTHETIC_ID,
    isRead: false,
    createdAt: new Date().toISOString(),
    data: {
      id: SUSPENSION_SYNTHETIC_ID,
      title: "Account Suspended — Payment Required",
      body: "Your platform access has been paused. Contact your agency to resolve the outstanding balance and restore service.",
      priority: "critical",
      isRead: false,
      createdAt: new Date().toISOString(),
      expiresAt: null,
    } as AdminAnnouncement,
  } : null;

  // +1 to badge when suspended (as fallback if DB announcement already got read)
  const unreadCount = Math.min(unread + (suspended ? 1 : 0), 99);

  // Prepend pinned suspension item to feed
  const displayFeed = suspensionItem
    ? [suspensionItem, ...feed.filter(f => !(f.kind === "announcement" && (f.data as AdminAnnouncement).title === "Account Suspended — Payment Required"))]
    : feed;

  return (
    <div style={{ position: "relative" }} ref={panelRef}>
      <button
        data-testid="button-activity-bell"
        onClick={() => setOpen((v) => !v)}
        style={{
          position: "relative",
          width: 40,
          height: 40,
          borderRadius: "50%",
          border: open ? `1px solid ${AMBER}40` : `1px solid ${BORDER}`,
          background: open ? `${AMBER}10` : SURFACE,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.15s ease",
          flexShrink: 0,
        }}
        aria-label={`Activity center${unreadCount > 0 ? ` — ${unreadCount} unread` : ""}`}
      >
        <Bell size={17} color={open ? AMBER : "#a1a1aa"} />
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.span
              key="badge"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              data-testid="badge-activity-unread"
              style={{
                position: "absolute",
                top: -2,
                right: -2,
                minWidth: 16,
                height: 16,
                padding: "0 4px",
                borderRadius: 8,
                background: RED,
                color: "#fff",
                fontSize: 9,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "monospace",
                border: `2px solid ${BG}`,
              }}
            >
              {unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: -8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.97 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            data-testid="panel-activity-center"
            style={{
              position: "absolute",
              top: "calc(100% + 10px)",
              right: 0,
              width: 380,
              maxWidth: "calc(100vw - 24px)",
              background: SURFACE,
              border: `1px solid ${BORDER}`,
              borderRadius: 12,
              boxShadow: "0 20px 60px rgba(0,0,0,0.8)",
              zIndex: 9999,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              maxHeight: "70vh",
            }}
          >
            {/* Header */}
            <div style={{ padding: "14px 16px 12px", borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Megaphone size={15} color={AMBER} />
                <span style={{ fontFamily: "Poppins, sans-serif", fontWeight: 700, fontSize: 13, color: "#fff" }}>Activity Center</span>
                {unreadCount > 0 && (
                  <span style={{ background: `${RED}20`, color: RED, border: `1px solid ${RED}40`, borderRadius: 10, fontSize: 9, fontWeight: 700, padding: "1px 6px", fontFamily: "monospace" }}>
                    {unreadCount} new
                  </span>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {unreadCount > 0 && (
                  <button
                    data-testid="button-mark-all-read"
                    onClick={markAllRead}
                    disabled={markingAll}
                    style={{ background: "none", border: `1px solid ${BORDER}`, borderRadius: 6, padding: "3px 8px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, color: "#71717a", fontSize: 10, fontWeight: 600, transition: "all 0.1s" }}
                  >
                    <CheckCheck size={11} />
                    {markingAll ? "Marking…" : "Mark all read"}
                  </button>
                )}
                <button
                  data-testid="button-close-activity"
                  onClick={() => setOpen(false)}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#52525b", padding: 4, display: "flex", alignItems: "center" }}
                >
                  <X size={14} />
                </button>
              </div>
            </div>

            {/* Feed */}
            <div style={{ overflowY: "auto", flex: 1 }}>
              {loading ? (
                <div style={{ padding: 32, textAlign: "center", color: "#52525b", fontSize: 12 }}>Loading…</div>
              ) : displayFeed.length === 0 ? (
                <div style={{ padding: 40, textAlign: "center" }}>
                  <Bell size={28} color="#2a2a2a" style={{ marginBottom: 10 }} />
                  <p style={{ margin: "8px 0 0", color: "#52525b", fontSize: 12 }}>No activity yet</p>
                  <p style={{ margin: "4px 0 0", color: "#3f3f46", fontSize: 11 }}>New bookings, contacts, and deposits will appear here</p>
                </div>
              ) : (
                displayFeed.map((item) => (
                  <FeedItemRow
                    key={`${item.kind}-${item.id}`}
                    item={item}
                    onRead={item.id === SUSPENSION_SYNTHETIC_ID ? () => {} : markItemRead}
                    pinned={item.id === SUSPENSION_SYNTHETIC_ID}
                    adminToken={adminKey}
                    scope={scope}
                  />
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function FeedItemRow({
  item, onRead, pinned = false, adminToken, scope,
}: {
  item: FeedItem;
  onRead: (item: FeedItem) => void;
  pinned?: boolean;
  adminToken: string;
  scope: "studio" | "ngu";
}) {
  const isAnnouncement = item.kind === "announcement";
  const ann = isAnnouncement ? (item.data as AdminAnnouncement) : null;
  const evt = !isAnnouncement ? (item.data as ActivityEvent) : null;
  const accent = ann ? getAnnouncementAccent(ann.priority) : undefined;

  const evtMeta = evt?.metadata ? (() => { try { return JSON.parse(evt.metadata) as Record<string, unknown>; } catch { return null; } })() : null;
  const link = evt ? getEventLink(evt.eventType, adminToken, scope, evtMeta) : null;

  const handleClick = (e: React.MouseEvent) => {
    onRead(item);
    if (link && !(e.target as HTMLElement).closest("a")) {
      window.location.href = link;
    }
  };

  return (
    <div
      data-testid={`activity-item-${item.kind}-${item.id}`}
      onClick={handleClick}
      style={{
        padding: pinned ? "13px 16px" : "11px 16px",
        borderBottom: `1px solid ${pinned ? `${RED}30` : BORDER}`,
        borderLeft: pinned ? `3px solid ${RED}` : "none",
        cursor: (item.isRead && !link && !pinned) ? "default" : "pointer",
        background: pinned ? `${RED}0a` : item.isRead ? "transparent" : `${SURFACE2}`,
        transition: "background 0.1s",
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        position: "relative",
      }}
    >
      {pinned && (
        <div style={{ position: "absolute", top: 6, right: 10, fontSize: 8, fontWeight: 800, color: RED, fontFamily: "monospace", letterSpacing: "0.1em", background: `${RED}20`, border: `1px solid ${RED}40`, padding: "1px 5px", borderRadius: 3 }}>
          PINNED
        </div>
      )}
      {/* Unread dot */}
      {!item.isRead && (
        <div style={{ position: "absolute", left: 6, top: "50%", transform: "translateY(-50%)", width: 5, height: 5, borderRadius: "50%", background: isAnnouncement ? accent : GREEN, flexShrink: 0 }} />
      )}

      {/* Icon */}
      <div style={{ width: 28, height: 28, borderRadius: 8, background: isAnnouncement ? `${accent}15` : "#1a1a1a", border: `1px solid ${isAnnouncement ? `${accent}30` : BORDER}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
        {isAnnouncement ? getAnnouncementIcon(ann!.priority) : getEventIcon(evt!.eventType)}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {isAnnouncement ? (
          <>
            <p style={{ margin: "0 0 2px", fontSize: 12, fontWeight: 700, color: accent, fontFamily: "Poppins, sans-serif", lineHeight: 1.3 }}>{ann!.title}</p>
            <p style={{ margin: "0 0 4px", fontSize: 11, color: "#a1a1aa", lineHeight: 1.5 }}>{ann!.body}</p>
          </>
        ) : (
          <p style={{ margin: "0 0 4px", fontSize: 12, color: item.isRead ? "#71717a" : "#e4e4e7", lineHeight: 1.5, fontWeight: item.isRead ? 400 : 500 }}>{evt!.summary}</p>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
          <span style={{ fontSize: 10, color: "#52525b", fontFamily: "monospace" }}>{timeAgo(item.createdAt)}</span>
          {link && (
            <a
              href={link}
              data-testid={`link-activity-view-${item.kind}-${item.id}`}
              onClick={(e) => { e.stopPropagation(); onRead(item); }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                fontSize: 10,
                fontWeight: 700,
                color: "#a78bfa",
                background: "#7c3aed18",
                border: "1px solid #7c3aed44",
                borderRadius: 5,
                padding: "1px 6px",
                textDecoration: "none",
                transition: "opacity 0.1s",
                letterSpacing: "0.01em",
                flexShrink: 0,
              }}
            >
              View <ArrowRight size={9} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
